import {UIGameManager} from '../UIGameManager'
import {PIXI} from '@colonist/pixi'
import {SectionBgAdaptiveView} from '../../ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameCardContainerView} from '../ui_game_card/UIGameCardContainerView'
import {UIGamePopupConfirmationButtons} from './UIGamePopupConfirmationButtons'
import {UIGamePlayerAvatarView} from '../ui_game_players/UIGamePlayerAvatarView'
import {TradeArrowView} from '../../ui_canvas_engine/ArrowView'
import {ArrayUtils} from '../../../../../shared/utils/ArrayUtils'
import {SelectCardFormat} from '@colonist/game-card-types'
import {UIGameCanvasController} from '../UIGameCanvasController'
import {CardEnum, PlayerColor} from '@colonist/game-states'

export abstract class UIGamePopupSelectCardToGiveToPlayer extends SectionBgAdaptiveView {
    protected readonly uiGameManager: UIGameManager
    protected readonly popupImageAndDescription: PIXI.Container
    private readonly playerReceivingCard: PlayerColor
    protected selectedCardView: UIGameCardContainerView
    protected cardsToSelectFrom: UIGameCardContainerView
    protected confirmationButtons: UIGamePopupConfirmationButtons
    private readonly selectCardFormat: SelectCardFormat
    private selectionRectangle: PIXI.Graphics
    private tradeArrow: TradeArrowView

    protected readonly cancelAction: () => void
    protected readonly padding: number
    protected readonly canvas: UIGameCanvasController

    constructor(uiGameManager: UIGameManager, popupImageAndDescription: PIXI.Container, selectCardFormat: SelectCardFormat, cancelAction: () => void, playerReceivingCard: PlayerColor) {
        super()
        this.uiGameManager = uiGameManager
        this.selectCardFormat = selectCardFormat
        this.playerReceivingCard = playerReceivingCard
        this.cancelAction = cancelAction

        this.canvas = this.uiGameManager.canvasController
        this.padding = this.canvas.itemSize * 0.05
        this.canvas.app.stage.addChild(this)

        this.popupImageAndDescription = popupImageAndDescription
        this.addChild(this.popupImageAndDescription)

        this.createSelectedCardView()
        this.createSelectedCardOutline()
        this.createTradeArrow()
        this.createAvatarView()
        this.createPlayerCardContainerView()
        this.setupPlayerCardContainerView()
        this.createConfirmationButtons()
        this.drawTextureBgAroundContent()
        this.setContainerY()
    }

    protected abstract createSelectedCardView()

    private createSelectedCardOutline() {
        this.selectionRectangle = new PIXI.Graphics()
        this.selectionRectangle.lineStyle(2, 0x212121)
        const rectanglePadding = this.canvas.margin
        const rectangleX = this.selectedCardView.height * 0.1 + this.canvas.margin + rectanglePadding
        const rectangleY = this.selectedCardView.cardY + rectanglePadding
        const rectangleW = this.selectedCardView.cardW - 2 * rectanglePadding
        const rectangleH = this.selectedCardView.cardH - 2 * rectanglePadding
        this.selectionRectangle.drawRect(rectangleX, rectangleY, rectangleW, rectangleH)
        this.selectedCardView.addChild(this.selectionRectangle)
    }

    private createTradeArrow() {
        const arrowSize = this.selectedCardView.height * 0.65
        const tradeArrowX = this.selectionRectangle.x + this.selectionRectangle.width + this.padding * 4
        this.tradeArrow = new TradeArrowView(tradeArrowX, 0, arrowSize, this.selectedCardView, false, true)
    }

    private createAvatarView() {
        const playerState = this.uiGameManager.gameController.getPlayerState(this.playerReceivingCard)
        const playerAvatarX = this.tradeArrow.x + this.tradeArrow.width + this.padding
        const showName = true
        new UIGamePlayerAvatarView(playerAvatarX, 0, this.selectedCardView.height, this.selectedCardView, playerState, showName)
    }

    protected abstract createPlayerCardContainerView()

    private setupPlayerCardContainerView() {
        const animate = false
        const allCardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor)
        this.cardsToSelectFrom.addMultipleCards(allCardsInHand, animate)
        this.cardsToSelectFrom.fadeInAllCardsExcludingCardsOfType(this.selectCardFormat.validCardsToSelect)
        this.cardsToSelectFrom.setCardClickAction((cardEnum: CardEnum) => {
            this.clickedCardToSelectFrom(cardEnum)
        })
    }

    protected abstract createConfirmationButtons()

    protected abstract setContainerY()

    private clickedCardToSelectFrom(cardEnum: CardEnum) {
        if(!ArrayUtils.elementInArray(this.selectCardFormat.validCardsToSelect, cardEnum)) return
        if(this.selectCardFormat.amountOfCardsToSelect == 1) this.resetCardContainers()
        if(this.selectedEnoughCards()) return
        this.selectedCardView.addNewCardOfType(cardEnum)
        this.cardsToSelectFrom.removeCard(cardEnum)
        this.updateCheckmark()
    }

    protected clickedSelectedCard(cardEnum: CardEnum) {
        this.selectedCardView.removeCard(cardEnum)
        this.cardsToSelectFrom.addNewCardOfType(cardEnum)
        this.updateCheckmark()
    }

    protected clickedCheckAction() {
        if(!this.selectedEnoughCards()) return
        const selectedResources = this.selectedCardView.cardInventory.getAllCardEnums()
        this.uiGameManager.socketGameSend.selectCards(selectedResources)
        this.hide()
    }

    private selectedEnoughCards() {
        const cardCount = this.selectedCardView.cardInventory.cardViews.length
        if(cardCount == this.selectCardFormat.amountOfCardsToSelect) return true
        return false
    }

    private resetCardContainers() {
        const selectedCards = this.selectedCardView.cardInventory.getAllCardEnums()
        for(const selectedCard of selectedCards) {
            this.selectedCardView.removeCard(selectedCard)
            this.cardsToSelectFrom.addNewCardOfType(selectedCard)
        }
    }

    private updateCheckmark() {
        this.confirmationButtons.checkButton.inactivateOpacity()

        if(!this.selectedEnoughCards()) return
        this.confirmationButtons.checkButton.activateOpacity()
    }

    hide() {
        if(this.parent != undefined) this.parent.removeChild(this)
        if(this.cardsToSelectFrom.parent != undefined) this.cardsToSelectFrom.parent.removeChild(this.cardsToSelectFrom)
    }

    showCardsToSelectFrom() {
        if(this.cardsToSelectFrom.parent != undefined) return
        this.uiGameManager.canvasController.app.stage.addChild(this.cardsToSelectFrom)
    }
}
